import React from "react";
import { FiCpu, FiWind, FiUsers, FiZap } from "react-icons/fi";
import { AqiGauge, MiniBar, aqiMeta, co2Color } from "./aqmHelpers";

const PRIORITY = {
  High:   { color: "#ef4444", bg: "#fee2e2" },
  Medium: { color: "#f59e0b", bg: "#fef3c7" },
  Low:    { color: "#3b82f6", bg: "#dbeafe" },
};

const AqmIndoorAiPanel = ({ d }) => {
  const { summary, aiInsights, floors, hvac } = d;
  const allRooms = floors.flatMap(f => f.rooms);
  const poorRooms = allRooms.filter(r => r.aqi > 100);
  const avgCo2 = Math.round(allRooms.reduce((s, r) => s + r.co2, 0) / allRooms.length);

  return (
    <div className="aqm-ai-panel">

      {/* Score cards */}
      <div className="aqm-ai-scores">
        <div className="aqm-ai-score-card">
          <AqiGauge aqi={summary.overallAqi} size={90} />
          <div>
            <div className="aqm-ai-sc-title">Indoor Air Health Score</div>
            <div className="aqm-ai-sc-sub">{100 - summary.overallAqi > 50 ? "Good" : "Needs Attention"} — Based on AQI, CO₂, TVOC and PM2.5 across all {d.site.totalRooms} rooms</div>
          </div>
        </div>
        <div className="aqm-ai-score-card">
          <AqiGauge aqi={Math.min(100, Math.round((1 - avgCo2 / 2000) * 100))} size={90} />
          <div>
            <div className="aqm-ai-sc-title">Ventilation Efficiency</div>
            <div className="aqm-ai-sc-sub">Average CO₂: <strong style={{ color: co2Color(avgCo2) }}>{avgCo2} ppm</strong> across building. HVAC energy saving: {hvac.energySavingPct}%</div>
          </div>
        </div>
        <div className="aqm-ai-savings-card">
          <div className="aqm-ai-sav-val">{poorRooms.length}</div>
          <div className="aqm-ai-sav-lbl">Rooms Need Attention</div>
          <div className="aqm-ai-sav-sub">AQI &gt; 100 — ventilation or cleaning recommended</div>
          <div className="aqm-ai-sav-money">≈ ₹{Math.round(hvac.energySavingPct * 120)}/day energy savings via AI HVAC</div>
        </div>
      </div>

      {/* AI Capability modules */}
      <div className="aqm-ai-modules">
        <div className="aqm-aim-card" style={{ borderTopColor: "#667eea" }}>
          <div className="aqm-aim-head"><FiCpu size={16} color="#667eea" /> AI Capabilities</div>
          <div className="aqm-aim-items">
            {[
              { label: "CO₂ Spike Detection",        score: 94, col: "#10b981" },
              { label: "Occupancy Correlation",       score: 88, col: "#10b981" },
              { label: "HVAC Auto-Optimization",      score: 82, col: "#10b981" },
              { label: "PM2.5 Source Identification", score: 71, col: "#f59e0b" },
            ].map(item => (
              <div key={item.label} className="aqm-aim-row">
                <span>{item.label}</span>
                <MiniBar value={item.score} max={100} color={item.col} height={6} />
                <span style={{ color: item.col, minWidth: 36, textAlign: "right" }}>{item.score}%</span>
              </div>
            ))}
          </div>
        </div>

        <div className="aqm-aim-card" style={{ borderTopColor: "#f59e0b" }}>
          <div className="aqm-aim-head"><FiUsers size={16} color="#f59e0b" /> Occupancy Insights</div>
          <div className="aqm-aim-items">
            {floors.map(fl => (
              <div key={fl.id} className="aqm-aim-row">
                <span style={{ fontSize: 11 }}>{fl.name}</span>
                <MiniBar value={fl.overallAqi} max={200} color={aqiMeta(fl.overallAqi).color} height={6} />
                <span style={{ color: aqiMeta(fl.overallAqi).color, minWidth: 36, textAlign: "right" }}>{fl.overallAqi}</span>
              </div>
            ))}
            <div className="aqm-aim-note" style={{ marginTop: 8 }}>
              Meeting Room 3 shows daily CO₂ peak at 15:00 — 8-person meeting pattern detected.
            </div>
          </div>
        </div>

        <div className="aqm-aim-card" style={{ borderTopColor: "#10b981" }}>
          <div className="aqm-aim-head"><FiWind size={16} color="#10b981" /> Ventilation Health</div>
          <div className="aqm-aim-suggestions">
            <div className="aqm-aim-sug">💡 Zone 3 (Open Office): Increase to High fan speed 09:00–18:00 daily — saves 28% CO₂ buildup</div>
            <div className="aqm-aim-sug">💡 Server Room: Reduce damper to 15% — CO₂ only 380 ppm, over-ventilated</div>
            <div className="aqm-aim-sug">💡 Cafeteria exhaust: Pre-activate 15 min before lunch (12:15 PM) to prevent PM2.5 spike</div>
            <div className="aqm-aim-total">Estimated daily HVAC saving: ₹{Math.round(hvac.energySavingPct * 125)}</div>
          </div>
        </div>

        <div className="aqm-aim-card" style={{ borderTopColor: "#ef4444" }}>
          <div className="aqm-aim-head"><FiZap size={16} color="#ef4444" /> Health Risk Prediction</div>
          <div className="aqm-aim-items">
            {[
              { label: "Headache Risk (CO₂)",    risk: 35, trend: "↑ Trending up", col: "#f59e0b" },
              { label: "Respiratory (PM2.5)",     risk: 22, trend: "→ Stable",      col: "#10b981" },
              { label: "Eye Irritation (TVOC)",   risk: 18, trend: "→ Stable",      col: "#10b981" },
              { label: "Thermal Discomfort",      risk: 12, trend: "→ Stable",      col: "#10b981" },
            ].map(item => (
              <div key={item.label} className="aqm-aim-row">
                <span style={{ fontSize: 11 }}>{item.label}</span>
                <span className="aqm-aim-trend" style={{ color: item.col }}>{item.trend}</span>
                <span style={{ color: item.col, fontSize: 11, minWidth: 56, textAlign: "right" }}>Risk: {item.risk}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Insight cards */}
      <div className="aqm-section-title" style={{ marginTop: 20 }}>Active AI Recommendations</div>
      <div className="aqm-insight-list">
        {aiInsights.map(ins => {
          const pm = PRIORITY[ins.priority];
          return (
            <div key={ins.id} className="aqm-insight-card" style={{ borderLeftColor: pm.color }}>
              <div className="aqm-ins-top">
                <span className="aqm-ins-icon">{ins.icon}</span>
                <div>
                  <div className="aqm-ins-title">{ins.title}</div>
                  <span className="aqm-badge" style={{ background: pm.bg, color: pm.color, fontSize: 11 }}>{ins.priority} Priority</span>
                </div>
              </div>
              <div className="aqm-ins-text">{ins.insight}</div>
              <div className="aqm-ins-footer">
                <span style={{ fontSize: 11, color: "#9ca3af" }}>{ins.id}</span>
                <button className="aqm-ctrl-btn aqm-ctrl-purple" style={{ padding: "4px 12px", fontSize: 12 }}>{ins.action}</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AqmIndoorAiPanel;
